import { genericFind } from '@/services/common';
import { promiseError } from '@/store/utils';
import {
  IListItem,
  IComponentList,
  IMaterialList,
  IProduct,
  IResourceComponent,
  IResourceMaterial,
  IStateBatch,
} from './types';

const productFind = genericFind('product');

const listAggregator = <ListType, Resource>(
  state: IStateBatch,
  plural: 'components' | 'materials',
  singular: 'component' | 'material',
): ListType => (
  state.batchList
    .filter(list => list.id)
    .reduce((resources: any, batchItem) => {
      // find product from list of products
      const product = state.products.find(p => p.id === batchItem.id);

      // aggregate product resources
      product && (product as any)[plural]
        .forEach((resource: Resource|any) => {
          const property = resource[singular];
          const { id } = property;
          const qty = +resource.qty * (batchItem.qty || 0);

          // if resource is in list of resources, add to qty
          // else create new resource record with initial qty
          resources[id]
            ? resources[id].qty = resources[id].qty + qty
            : resources[id] = { [singular]: property, qty };
        });

      return resources;
    }, {} as ListType)
);

export default () => ({
  namespaced: true,
  state: (): IStateBatch => ({
    products: [],
    batchList: [],
  }),
  getters: {
    componentList: (state: IStateBatch) => (
      listAggregator<IComponentList, IResourceComponent>(state, 'components', 'component')
    ),
    materialList: (state: IStateBatch) => (
      listAggregator<IMaterialList, IResourceMaterial>(state, 'materials', 'material')
    ),
    productList: (state: IStateBatch) => (
      state.products.reduce((p, c) => {
        p[c.id] = c.name;
        return p;
      }, {} as { [id: string]: string })
    ),
  },
  mutations: {
    BATCH_LIST_ADD: (state: IStateBatch) => {
      state.batchList = state.batchList.concat({
        id: '',
        qty: null,
      });
    },
    BATCH_LIST_UPDATE: (
      state: IStateBatch,
      batchList: IListItem[],
    ) => {
      state.batchList = batchList;
    },
    LOAD_PRODUCTS: (
      state: IStateBatch,
      products: IProduct[],
    ) => {
      state.products = products;
    },
  },
  actions: {
    addToBatchList: ({ commit }: any) => commit('BATCH_LIST_ADD'),
    loadProducts: ({ commit }: any) => (
      productFind(`
        id
        name
        components {
          qty
          component {
            id
            name
          }
        }
        materials {
          qty
          material {
            id
            name
            composition
            color
          }
        }`, {
          limit: 1000,
        })
        .then(
          products => commit('LOAD_PRODUCTS', products.result),
          promiseError('LOAD_PRODUCTS', commit),
        )
    ),
    updateBatchList: ({ commit }: any, payload: IListItem[]) => (
      commit('BATCH_LIST_UPDATE', payload)
    ),
  },
});
